import Phaser from 'phaser'
import { Capacitor } from '@capacitor/core'
import { SettingsManager } from '../game/SettingsManager'
import { FontConfig, createTextStyle, SoftButtonColors } from '../config/UIConfig'
import { GameCenter } from '../native/GameCenter'
import { Analytics } from '../utils/Analytics'
import { GameStateSaver } from '../utils/GameStateSaver'
import { ScreenshotMode } from '../utils/ScreenshotMode'
import { ResponsiveLayout } from '../utils/ResponsiveLayout'
import { MenuGamepadNavigator } from '../ui/MenuGamepadNavigator'
import { SoftButton } from '../ui/SoftButton'
import { Logger } from '../utils/Logger'

export default class Title extends Phaser.Scene {
  private settingsManager: SettingsManager
  private menuButtons: Phaser.GameObjects.Container[] = []
  private softButtons: SoftButton[] = []
  private titleText!: Phaser.GameObjects.Text
  private gameCenterButtons: Phaser.GameObjects.GameObject[] = []
  private gamepadNav?: MenuGamepadNavigator

  constructor() {
    super('Title')
    this.settingsManager = SettingsManager.getInstance()
  }

  async create() {
    Logger.getInstance().log('Title scene: create() method started')

    // Initialize ScreenshotMode
    await ScreenshotMode.initialize()

    // Guard: if this scene was stopped while we were awaiting (e.g. Maestro's
    // safeStart navigated elsewhere, or a concurrent lifecycle transition
    // fired), do not attempt any further navigation or UI creation from this
    // stale invocation — it would race with whatever scene is now active.
    if (!this.scene.isActive()) return

    // Check if we should auto-navigate to game for screenshots
    if (ScreenshotMode.shouldAutoNavigate()) {
      Logger.getInstance().log('📸 Screenshot mode detected - auto-navigating to game')
      // Skip title screen and go straight to game
      this.time.delayedCall(500, () => {
        this.scene.start('ModeSelection')
      })
      return // Don't create title screen UI
    }

    // Check if there's a saved game AND the app was recently backgrounded
    // If so, auto-resume instead of showing title screen
    const hasSavedGame = GameStateSaver.hasSavedGame()
    if (hasSavedGame) {
      const wasRecentlyBackgrounded = this.checkIfRecentlyBackgrounded()
      const wasManualQuit = localStorage.getItem('manualQuit') === 'true'

      // Only auto-resume if recently backgrounded AND not a manual quit
      if (wasRecentlyBackgrounded && !wasManualQuit) {
        Logger.getInstance().log('Title scene: Auto-resuming saved game (app was recently backgrounded)')
        // Signal that music should start when Game scene loads
        this.registry.set('startMusic', true)
        this.scene.start('Game', { loadSavedGame: true })
        return // Skip showing title screen
      }

      // Note: Don't clear manualQuit flag here - it should persist until user takes action
      // The flag will be cleared when user starts a new game or loads the saved game
    }

    // Track screen view
    Analytics.trackScreen('Title')

    this.createUI()

    // Add resize handler
    this.scale.on('resize', this.handleResize, this)

    // Add keyboard support
    Logger.getInstance().log('Title scene: Adding keyboard support')
    this.input.keyboard!.on('keydown-ENTER', () => {
      this.scene.start('Game')
    })

    this.input.keyboard!.on('keydown-SPACE', () => {
      this.scene.start('Game')
    })

    // Add learn keyboard shortcut
    this.input.keyboard!.on('keydown-L', () => {
      this.scene.start('Learn', { returnTo: 'Title' })
    })

    // Add help keyboard shortcut
    this.input.keyboard!.on('keydown-H', () => {
      this.scene.start('Help')
    })

    // Add settings keyboard shortcut
    this.input.keyboard!.on('keydown-S', () => {
      this.scene.start('Settings', { returnTo: 'Title' })
    })

    Logger.getInstance().log('Title scene: create() method completed successfully')
  }

  private createUI(): void {
    const w = this.scale.width
    const h = this.scale.height
    const responsive = ResponsiveLayout.getConfig(this)
    const margins = ResponsiveLayout.getMargins(this)

    Logger.getInstance().log('Title scene: Screen dimensions:', w, 'x', h, 'Breakpoint:', responsive.breakpoint)

    // Background - use selected background from settings
    Logger.getInstance().log('Title scene: Adding background image')
    const backgroundSetting = this.settingsManager.getBackgroundImage()
    const backgroundKey = backgroundSetting === 'default' ? 'bg' : `bg-${backgroundSetting}`
    const bg = this.add.image(w / 2, h / 2, backgroundKey)
    // Scale to cover entire screen, cropping excess to avoid black bars
    const bgWidth = this.registry.get('bgWidth') || 1080
    const bgHeight = this.registry.get('bgHeight') || 1920
    const scaleX = w / bgWidth
    const scaleY = h / bgHeight
    const scale = Math.max(scaleX, scaleY)
    bg.setScale(scale)

    // Title - responsive font size and position
    Logger.getInstance().log('Title scene: Adding title text')
    const titleFontSize = ResponsiveLayout.getFontSize(this, 64)
    const titleY = responsive.isPortrait ? h * 0.15 : h * 0.22 + 30
    this.titleText = this.add.text(w / 2, titleY, 'Cell Swap!',
      createTextStyle('title', { fontSize: `${titleFontSize}px`, color: '#6e2d55' })
    ).setOrigin(0.5)

    // Get saved game status
    const hasSavedGame = GameStateSaver.hasSavedGame()

    // Button sizes - primary (New Game / Continue) is wider, secondary buttons are narrower
    const primarySize = ResponsiveLayout.getButtonSize(this, 240, 58)
    const secondarySize = ResponsiveLayout.getButtonSize(this, 200, 48)

    // Helper to create soft menu buttons
    const createMenuButton = (
      y: number,
      label: string,
      baseColor: number,
      onClick: () => void,
      isPrimary: boolean = false,
      customFontSize?: number
    ) => {
      const size = isPrimary ? primarySize : secondarySize
      const defaultFont = isPrimary ? 26 : 20
      const buttonFontSize = customFontSize
        ? ResponsiveLayout.getFontSize(this, customFontSize)
        : ResponsiveLayout.getFontSize(this, defaultFont)
      const btn = new SoftButton(this, {
        x: w / 2,
        y,
        width: size.width,
        height: size.height,
        shape: 'pill',
        color: baseColor,
        label,
        fontSize: buttonFontSize,
        onClick,
      })
      this.menuButtons.push(btn.container)
      this.softButtons.push(btn)
      return btn
    }

    Logger.getInstance().log('Title scene: Adding menu buttons')
    // Unified monochromatic palette derived from background theme (pink/plum spectrum)
    const buttonPalette = {
      continue: { base: SoftButtonColors.menuContinue },
      start: { base: SoftButtonColors.menuStart },
      learn: { base: SoftButtonColors.menuLearn },
      help: { base: SoftButtonColors.menuHelp },
      settings: { base: SoftButtonColors.menuSettings }
    }

    // Compact button layout – tight spacing inspired by Birdigo / Knot Words
    // Primary buttons get slightly more breathing room than secondary ones
    const primaryGap = responsive.isPortrait
      ? Math.min(h * 0.065, 56)
      : Math.min(h * 0.07, 60)
    const secondaryGap = responsive.isPortrait
      ? Math.min(h * 0.045, 40)
      : Math.min(h * 0.05, 44)

    // Calculate total button group height to center it in available space
    const numPrimary = hasSavedGame ? 2 : 1
    const numSecondary = 3 // Learn, Help, Settings
    const totalGroupHeight =
      numPrimary * primarySize.height +
      numSecondary * secondarySize.height +
      (numPrimary - 1) * primaryGap +       // gaps between primary buttons
      primaryGap +                            // gap between primary and secondary group
      (numSecondary - 1) * secondaryGap       // gaps between secondary buttons

    // Center the button group between title bottom and bottom of screen (with some padding)
    const titleBottom = titleY + titleFontSize * 0.6
    const bottomPad = h * 0.12 // leave room for Game Center buttons
    const availableSpace = h - bottomPad - titleBottom
    const startY = titleBottom + (availableSpace - totalGroupHeight) / 2 + primarySize.height / 2
    let currentY = startY

    if (hasSavedGame) {
      Logger.getInstance().log('Title scene: Adding continue button (saved game found)')
      const continueFontSize = Capacitor.isNativePlatform() ? 24 : 20
      createMenuButton(currentY, 'Continue Game', buttonPalette.continue.base, () => {
        Logger.getInstance().log('Continue button clicked - loading saved game')
        localStorage.removeItem('manualQuit')
        this.registry.set('startMusic', true)
        this.scene.start('Game', { loadSavedGame: true })
      }, true, continueFontSize)
      currentY += primaryGap + primarySize.height
    }

    createMenuButton(currentY, 'New Game', buttonPalette.start.base, () => {
      localStorage.removeItem('manualQuit')
      this.registry.set('startMusic', true)
      this.scene.start('ModeSelection')
    }, true)
    // Gap between primary and secondary group (use half-heights since buttons are center-anchored)
    currentY += primaryGap + primarySize.height / 2 + secondarySize.height / 2

    Logger.getInstance().log('Title scene: Adding secondary buttons')
    createMenuButton(currentY, 'Learn', buttonPalette.learn.base, () => {
      this.scene.start('Learn', { returnTo: 'Title' })
    })
    currentY += secondaryGap + secondarySize.height

    createMenuButton(currentY, 'Help', buttonPalette.help.base, () => {
      this.scene.start('Help')
    })
    currentY += secondaryGap + secondarySize.height

    createMenuButton(currentY, 'Settings', buttonPalette.settings.base, () => {
      this.scene.start('Settings', { returnTo: 'Title' })
    })

    // Add Game Center buttons (top-right corner)
    this.createGameCenterButtons(w, h)

    // Gamepad navigation for menu buttons
    this.gamepadNav?.destroy()
    this.gamepadNav = new MenuGamepadNavigator(this)
    for (const btn of this.softButtons) {
      this.gamepadNav.addItem(btn.getHitArea(), () => btn.getHitArea().emit('pointerup'))
    }
  }

  /**
   * Create Game Center buttons for leaderboards and achievements
   */
  private createGameCenterButtons(w: number, h: number): void {
    // Clear existing buttons
    this.gameCenterButtons.forEach(btn => btn.destroy())
    this.gameCenterButtons = []

    // Always show Game Center buttons (they'll show a message if not available)
    const isAvailable = GameCenter.isGameCenterAvailable()
    Logger.getInstance().log('Creating Game Center buttons, available:', isAvailable)

    const responsive = ResponsiveLayout.getConfig(this)
    const margins = ResponsiveLayout.getMargins(this)
    const safeArea = ResponsiveLayout.getSafeArea()

    const buttonSize = responsive.isPortrait ? 45 : 50
    const padding = responsive.isPortrait ? 10 : 15
    const bottomMargin = Math.max(20, safeArea.bottom) + margins.vertical
    const rightMargin = Math.max(padding, safeArea.right) + margins.horizontal

    // Helper to create icon buttons
    const createIconButton = (
      x: number,
      y: number,
      icon: string,
      label: string,
      onClick: () => void
    ) => {
      const btn = new SoftButton(this, {
        x,
        y,
        width: buttonSize,
        shape: 'circle',
        color: SoftButtonColors.gameCenter,
        icon,
        iconSize: ResponsiveLayout.getFontSize(this, 24),
        onClick,
      })
      this.gameCenterButtons.push(btn.container)

      // Tooltip
      const tooltipFontSize = ResponsiveLayout.getFontSize(this, 12)
      const tooltip = this.add.text(x, y + buttonSize / 2 + 20, label,
        createTextStyle('label', {
          fontSize: `${tooltipFontSize}px`,
          backgroundColor: '#000000',
          padding: { x: 6, y: 4 }
        })
      ).setOrigin(0.5).setVisible(false).setDepth(1000)
      this.gameCenterButtons.push(tooltip)

      const hitArea = btn.getHitArea()
      hitArea.on('pointerover', () => {
        tooltip.setVisible(true)
      })
      hitArea.on('pointerout', () => {
        tooltip.setVisible(false)
      })
    }

    // Leaderboard button (trophy icon)
    createIconButton(
      w - padding - buttonSize / 2 - buttonSize - padding,
      h - bottomMargin - buttonSize / 2,
      '🏆',
      'Leaderboards',
      () => {
        if (!isAvailable) {
          this.showNotSupportedDialog('Game Center is not available. This feature requires an iOS device with Game Center enabled.')
          return
        }
        Logger.getInstance().log('Opening Game Center leaderboards')
        GameCenter.showLeaderboard()
      }
    )

    // Achievements button (star icon)
    createIconButton(
      w - padding - buttonSize / 2,
      h - bottomMargin - buttonSize / 2,
      '⭐',
      'Achievements',
      () => {
        if (!isAvailable) {
          this.showNotSupportedDialog('Game Center is not available. This feature requires an iOS device with Game Center enabled.')
          return
        }
        Logger.getInstance().log('Opening Game Center achievements')
        GameCenter.showAchievements()
      }
    )

    // Show authentication status (small indicator)
    const statusText = isAvailable
      ? (GameCenter.isPlayerAuthenticated() ? '✓ Connected' : '○ Sign In')
      : '○ Not Available'

    const authStatus = this.add.text(
      w - padding - buttonSize - padding - buttonSize / 2,
      h - bottomMargin - buttonSize - 10,
      statusText,
      createTextStyle('label', {
        fontSize: '10px',
        color: isAvailable && GameCenter.isPlayerAuthenticated() ? '#4ade80' : '#94a3b8'
      })
    ).setOrigin(0.5)

    // If not available yet, wait for initialization and update status
    if (!isAvailable) {
      // Start polling for Game Center availability
      const pollInterval = this.time.addEvent({
        delay: 1000, // Check every second
        callback: () => {
          const nowAvailable = GameCenter.isGameCenterAvailable()
          if (nowAvailable) {
            Logger.getInstance().log('Game Center became available!')
            // Stop polling
            pollInterval.destroy()

            // Update status
            const newStatusText = GameCenter.isPlayerAuthenticated() ? '✓ Connected' : '○ Sign In'
            authStatus.setText(newStatusText)
            authStatus.setColor(GameCenter.isPlayerAuthenticated() ? '#4ade80' : '#94a3b8')

            // If authenticated, we're done
            if (GameCenter.isPlayerAuthenticated()) {
              return
            }

            // Otherwise allow clicking to authenticate
            authStatus.setInteractive({ useHandCursor: true })
            authStatus.on('pointerup', async () => {
              Logger.getInstance().log('Attempting Game Center authentication...')
              const success = await GameCenter.authenticatePlayer()
              if (success) {
                authStatus.setText('✓ Connected')
                authStatus.setColor('#4ade80')
                authStatus.setInteractive(false)
              }
            })
          }
        },
        loop: true
      })

      // Stop polling after 10 seconds to avoid infinite checking
      this.time.delayedCall(10000, () => {
        if (pollInterval.paused === false) {
          Logger.getInstance().log('Game Center polling timed out after 10 seconds')
          pollInterval.destroy()
        }
      })
    } else if (!GameCenter.isPlayerAuthenticated()) {
      // Available but not authenticated - allow clicking to authenticate
      authStatus.setInteractive({ useHandCursor: true })
      authStatus.on('pointerup', async () => {
        Logger.getInstance().log('Attempting Game Center authentication...')
        const success = await GameCenter.authenticatePlayer()
        if (success) {
          authStatus.setText('✓ Connected')
          authStatus.setColor('#4ade80')
          authStatus.setInteractive(false)
        }
      })
    }
  }

  /**
   * Show a dialog when Game Center is not supported
   */
  private showNotSupportedDialog(message: string): void {
    const w = this.scale.width
    const h = this.scale.height

    // Create overlay
    const overlay = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7)
    overlay.setInteractive()
    overlay.setDepth(9999)

    // Create dialog
    const dialogWidth = Math.min(400, w - 40)
    const dialogHeight = 200
    const dialogBg = this.add.rectangle(w / 2, h / 2, dialogWidth, dialogHeight, 0x2c3e50, 1)
    dialogBg.setStrokeStyle(3, 0xffffff, 0.8)
    dialogBg.setDepth(10000)

    // Title
    const title = this.add.text(w / 2, h / 2 - 50, 'Not Available',
      createTextStyle('title', {
        fontSize: '24px',
        color: '#e74c3c'
      })
    ).setOrigin(0.5).setDepth(10001)

    // Message
    const messageText = this.add.text(w / 2, h / 2, message,
      createTextStyle('label', {
        fontSize: '14px',
        color: '#ffffff',
        wordWrap: { width: dialogWidth - 40 },
        align: 'center'
      })
    ).setOrigin(0.5).setDepth(10001)

    // OK button
    const okButton = this.add.text(w / 2, h / 2 + 60, 'OK', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#3498db',
      padding: { x: 30, y: 10 },
      fontFamily: 'Arial, sans-serif'
    })
      .setOrigin(0.5)
      .setDepth(10002)
      .setInteractive({ useHandCursor: true })

    okButton.on('pointerover', () => {
      okButton.setStyle({ backgroundColor: '#5dade2' })
    })
    okButton.on('pointerout', () => {
      okButton.setStyle({ backgroundColor: '#3498db' })
    })
    okButton.on('pointerup', () => {
      // Clean up dialog
      overlay.destroy()
      dialogBg.destroy()
      title.destroy()
      messageText.destroy()
      okButton.destroy()
    })
  }

  /**
   * Check if the app was recently backgrounded (within last 5 minutes)
   */
  private checkIfRecentlyBackgrounded(): boolean {
    const BACKGROUND_THRESHOLD = 5 * 60 * 1000 // 5 minutes

    try {
      const lastSaveTime = localStorage.getItem('lastGameSaveTime')
      if (!lastSaveTime) {
        return false
      }

      const lastSave = parseInt(lastSaveTime, 10)
      const now = Date.now()
      const timeSinceLastSave = now - lastSave

      // If game was saved within last 5 minutes, likely the app was just backgrounded
      return timeSinceLastSave < BACKGROUND_THRESHOLD
    } catch (error) {
      console.warn('Failed to check background timestamp:', error)
      return false
    }
  }

  private handleResize(gameSize: Phaser.Structs.Size): void {
    // Clean up navigator before removing children (highlight is a child)
    this.gamepadNav?.destroy()
    this.gamepadNav = undefined

    // Clean up soft buttons
    this.softButtons.forEach(btn => btn.destroy())
    this.softButtons = []

    // Clear all UI elements
    this.children.removeAll(true)
    this.menuButtons = []
    this.gameCenterButtons = []

    // Recreate UI with new dimensions
    this.createUI()
  }

  shutdown() {
    // Clean up resize listener
    this.scale.off('resize', this.handleResize, this)
  }
}

